import { WAD } from "./risk";

/**
 * Morpho Blue interest accrual, mirrored in bigint so the frontend's debt
 * figure matches what the contract sees at execution time.
 *
 * Morpho stores `totalBorrowAssets` as of the market's `lastUpdate`; interest
 * keeps accruing off-chain until the next market touch. A borrower's debt is
 * `borrowShares × totalBorrowAssets / totalBorrowShares`, so sizing a full-debt
 * repay/swap off the un-accrued total understates it. We replay Morpho's own
 * `MathLib.wTaylorCompounded` accrual up to "now" before deriving the debt.
 *
 * Assumes `state.fee == 0` (true for the CADC/USDC market): with no fee Morpho
 * mints no new borrow shares on accrual, so only `totalBorrowAssets` grows.
 */

/** `a × b / WAD`, rounded down (Morpho's `wMulDown`). */
const wMulDown = (a: bigint, b: bigint): bigint => (a * b) / WAD;

/**
 * 3rd-order Taylor expansion of `e^(rate·elapsed) − 1`, matching Morpho's
 * `MathLib.wTaylorCompounded`. `ratePerSec` and the result are WAD-scaled.
 */
export const wTaylorCompounded = (
  ratePerSec: bigint,
  elapsed: bigint,
): bigint => {
  const firstTerm = ratePerSec * elapsed;
  const secondTerm = (firstTerm * firstTerm) / (2n * WAD);
  const thirdTerm = (secondTerm * firstTerm) / (3n * WAD);
  return firstTerm + secondTerm + thirdTerm;
};

type AccrualState = {
  readonly totalBorrowAssets: bigint;
  readonly lastUpdate: bigint;
};

/**
 * `totalBorrowAssets` accrued forward from `state.lastUpdate` to `nowSec` at
 * `borrowRatePerSec`. Returns the input unchanged when there's nothing to
 * borrow against or the clock hasn't advanced past `lastUpdate`.
 */
export const accruedTotalBorrowAssets = (
  state: AccrualState,
  borrowRatePerSec: bigint,
  nowSec: bigint,
): bigint => {
  if (state.totalBorrowAssets === 0n) return state.totalBorrowAssets;
  if (nowSec <= state.lastUpdate) return state.totalBorrowAssets;
  const elapsed = nowSec - state.lastUpdate;
  const interest = wMulDown(
    state.totalBorrowAssets,
    wTaylorCompounded(borrowRatePerSec, elapsed),
  );
  return state.totalBorrowAssets + interest;
};

/**
 * A borrower's current debt in loan-token assets, with interest accrued to
 * `nowSec`. `borrowShares` are unaffected by accrual on a zero-fee market.
 */
export const accruedBorrowAssets = (
  borrowShares: bigint,
  totalBorrowShares: bigint,
  accruedTotalAssets: bigint,
): bigint => {
  if (borrowShares === 0n || totalBorrowShares === 0n) return 0n;
  return (borrowShares * accruedTotalAssets) / totalBorrowShares;
};
